import './MyAccountContainer.css';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { CenterFrameLayout } from 'Layouts';
import { trackEvent, segmentTrackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { getCardPaymentMethodId, trackPaymentStarted } from 'Utils/PaymentMethods';
import { fetchProfileInfo } from 'State/User';
import { payment, fetchPaymentMethods } from 'State/PaymentMethods';
import { fetchPayments } from 'State/Payments';
import { getPlans, selectRestartPlan } from 'State/Plans';
import { fetchSubscriptions, fetchSubscriptionStatus } from 'State/Subscriptions';
import { reactivateSubscription } from 'Api/AccountApi';
import { ConfirmationMsg } from 'Components/ConfirmationMsg';
import { ManageProfile } from 'Features/MyAccount/ManageProfile';
import { Membership } from 'Features/MyAccount/Membership';
import { ManageBilling } from 'Features/MyAccount/ManageBilling';
import { RedfastZone } from 'Components/RedfastZone';
import { ManagePromotion } from 'Features/MyAccount/ManagePromotion';
import { RestartPlanModal } from 'Features/MyAccount/RestartPlanModal';
import { ResumePlanModal } from 'Features/MyAccount/ResumePlanModal';
import { UpdatePaymentModal } from 'Features/MyAccount/UpdatePaymentModal';
import { RestartConfirmationModal } from 'Features/MyAccount/RestartPlanChurned/RestartConfirmationModal';
import { PAYMENT_METHODS } from 'Features/PaymentMethodSelector/PaymentMethodSelector';
import { SectionComponent } from 'Features/MyAccount/SectionComponent';
import { applyPromoCode } from 'State/PromoCode';
import { usePauseSubscription } from 'Hooks/usePauseSubscription';
import { updateCardPayment } from 'Api/EvergentApi';
import { getNumberOfDaysForPause } from 'Utils/Date';
import { PromoBannerContainer } from 'Features/MyAccount/PromoBanner/PromoBannerContainer';
import BANNER_CONFIG from 'Config/BannerCatalog.json';
import { getActivePlanSimplifiedName, getPlanFromList } from 'Utils/Plans';
import { trackPage } from 'Utils/Analytics';
import { useLocation, useNavigate } from 'react-router-dom';
import { isEmptyObj } from 'Utils/Utils';
import { setPurchaseMode } from 'State/PurchaseMode';
import { MANAGE_PLAN_PURCHASE_MODE, MY_ACCOUNT_TOP, MY_ACCOUNT_2ND, MY_ACCOUNT_PAGE_TOP } from 'Utils/Constants';
import BrazeContentCard from 'Components/BrazeContent/BrazeContentCard';
import { hasBrazeContentCard } from 'Utils/Braze';
import { setLocation } from 'Utils/Path';
import { useStore } from 'Hooks/useStore';
import { clearConfirmation } from 'State/Layout';

const trackEditAccount = (itemTitle) => {
  segmentTrackEvent(TRACKING_ENUMS.EVENT_UPDATE_ACCOUNT_STARTED);
  trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_SETTING_SELECTED, {
    page_item_title: itemTitle,
  });
};

export function MyAccountContainer() {
  const [isLoading, setLoading] = useState(true);
  const [showReinstateModal, toggleReinstateModal] = useState(false);
  const [showRestartChurnedModal, toggleRestartChurnedModal] = useState(false);
  const [showResumePlanModal, toggleResumePlanModal] = useState(false);
  const [showRestartChurnedModalSuccess, toggleRestartChurnedModalSuccess] = useState(false);
  const [showApplyCouponPaymentModal, toggleApplyCouponPaymentModal] = useState(false);

  const paymentMethods = useStore('paymentMethods');
  const { couponCode = '' } = useStore('promoCode');
  const user = useStore('user');
  const subscription = useStore('subscription');
  const routes = useStore('appConfig.routes');
  const { isConfirmSuccess, confirmationMsg } = useStore('pageLayout');
  const payments = useStore('payments');
  const { hasPayments, activeBillInfo, nextBillInfo, orders } = payments;
  const { rootDomain, enablePauseSubscription, entitlementId, showAccountPromoCode } =
    useStore('appConfig.defaultConfig');
  const { allPlans } = useStore('plans');

  const navigate = useNavigate();
  const { state } = useLocation();
  const showPlanSelectModal = state?.showPlanSelectModal;
  const myAccountPageTopContentCard = hasBrazeContentCard(MY_ACCOUNT_PAGE_TOP);
  const myAccountTopBanner = hasBrazeContentCard(MY_ACCOUNT_TOP);
  const myAccount2ndBanner = hasBrazeContentCard(MY_ACCOUNT_2ND);
  const promoWrapperRef = useRef(null);

  const nextBillingDateTime = nextBillInfo && nextBillInfo.nextBillingDateTime;
  const {
    activeSubscriptionId,
    activeSubscriptionTier,
    isChurnedRestartAvailable,
    restartChurnedUserDetails,
    isPendingCancel,
    isExistingPlanUser,
    isIAPUser,
    accountPromotionSegment,
    nextSubscription,
    isSuccess,
    hasSubscription,
    isChangePlanAvailable,
    isFetchingStatus,
    isFetching,
    isStatusSuccess,
  } = subscription;
  const { restartPlan, paymentMethodRequired } = restartChurnedUserDetails;
  const { isAuthenticated } = user;

  const promoBanner = BANNER_CONFIG[accountPromotionSegment.segmentName] || false;

  let activePlanSimplifiedName = getActivePlanSimplifiedName(allPlans, activeSubscriptionId);
  if (!activePlanSimplifiedName && nextSubscription?.serviceID) {
    const nextPlanName = getActivePlanSimplifiedName(allPlans, nextSubscription.serviceID);
    if (nextPlanName) activePlanSimplifiedName = `Upcoming Plan: ${nextPlanName}`;
  }
  let activePlan = getPlanFromList(activeSubscriptionId, allPlans);
  activePlan = isEmptyObj(activePlan) ? null : activePlan;

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfileInfo();
    }
    return function unMountMyAccount() {
      clearConfirmation();
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscriptions();
      fetchSubscriptionStatus();
      fetchPayments(entitlementId);
      fetchPaymentMethods();
    }
    setLoading(false); //outside because anonymous/mvpd users are not isAuthenticated
  }, [entitlementId, isAuthenticated]);

  useEffect(() => {
    if (!isFetchingStatus && !isFetching && isStatusSuccess && isSuccess) {
      getPlans();
    }
  }, [isFetchingStatus, isFetching, isStatusSuccess, isSuccess]);

  // set manage purchaseMode for existing active subscription users.
  useEffect(() => {
    if (isSuccess && hasSubscription && isChangePlanAvailable) {
      setPurchaseMode(MANAGE_PLAN_PURCHASE_MODE);
    }
  }, [isSuccess, hasSubscription, isChangePlanAvailable]);

  useEffect(() => {
    if (accountPromotionSegment.segmentType === 'Military Segment Type') {
      trackPage('Account Upgrade Option Offer', window.location.pathname, 'account_upgrade_option_offer');
    }
  }, [accountPromotionSegment.segmentType]);

  const [pauseState, pauseActions] = usePauseSubscription(
    enablePauseSubscription && isExistingPlanUser && isAuthenticated && activeSubscriptionTier === 100
  );

  const isPauseEnabled = useMemo(() => {
    return pauseState.noOfDaysLeft >= getNumberOfDaysForPause(nextBillingDateTime, 1) && pauseState.noOfTimesLeft > 0;
  }, [pauseState, nextBillingDateTime]);

  const handleRestartChurnedPlan = async () => {
    const planDetails = {
      page_plan_id: restartPlan.planId,
      page_plan_name: restartPlan.displayName,
      page_plan_price: restartPlan.retailPrice,
    };

    const paymentMethodType = paymentMethodRequired ? 'card' : 'saved';
    const paymentmethodInfo = getCardPaymentMethodId(paymentMethods.cardPayment);
    trackPaymentStarted({
      page_start_source: 'my account',
      page_plan_flow_type: 'reinstate plan',
      page_payment_method: PAYMENT_METHODS.CARD,
    });
    return payment(
      paymentMethodType,
      { isSkipDefaultPromo: true, paymentmethodInfo },
      PAYMENT_METHODS.CARD,
      'my account',
      'reinstate plan'
    )
      .then(() => {
        trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_COMPLETED, planDetails);
        toggleRestartChurnedModalSuccess(true);
        toggleRestartChurnedModal(true);
      })
      .catch((error) => {
        return onSubmitReactFormError(error.message);
      });
  };

  const handleRestartPlan = async (showConfirmationModal = true) => {
    // churned user - add new order
    if (isChurnedRestartAvailable) {
      await selectRestartPlan(restartPlan);
      const churnedPlanDetails = {
        page_plan_id: restartPlan.planId,
        page_plan_name: restartPlan.displayName,
        page_plan_price: restartPlan.retailPrice,
      };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_STARTED, churnedPlanDetails);
      if (paymentMethodRequired) {
        return toggleRestartChurnedModal(true);
      }
      return handleRestartChurnedPlan();
    }

    // pending cancel user - reactivate an existing plan
    const planDetails = {
      page_plan_id: activeSubscriptionId || '',
      page_plan_name: (activeBillInfo && activeBillInfo.planName) || (nextBillInfo && nextBillInfo.planName) || '',
      page_plan_price: (activeBillInfo && activeBillInfo.planPrice) || (nextBillInfo && nextBillInfo.planPrice) || '',
    };
    trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_STARTED, planDetails);
    const { isSuccess } = await reactivateSubscription(activeSubscriptionId);
    if (isSuccess) {
      await fetchSubscriptions();
      await fetchSubscriptionStatus();
      await fetchPayments(entitlementId);
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESTART_PLAN_COMPLETED, planDetails);
    }
    return toggleReinstateModal(showConfirmationModal && isSuccess);
  };

  const handleResumeSubscription = async () => {
    const result = await pauseActions.resume(activeSubscriptionId);
    const pauseTrackingResumeTracking = {
      page_start_source: 'my account',
      page_plan_name: (activeBillInfo && activeBillInfo.planName) || (nextBillInfo && nextBillInfo.planName) || '',
      page_plan_price: (activeBillInfo && activeBillInfo.planPrice) || (nextBillInfo && nextBillInfo.planPrice) || '',
      page_plan_id: activeSubscriptionId,
    };

    if (result.isSuccess) {
      fetchSubscriptions();
      fetchSubscriptionStatus();
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_COMPLETED, pauseTrackingResumeTracking);
    } else {
      const errorObj = {
        page_error_description: 'Pause Resume Subscription Error',
        page_error_type: result,
      };
      const pauseResumeErrorTracking = { ...pauseTrackingResumeTracking, ...errorObj };
      trackEvent(TRACKING_ENUMS.EVENT_ACCOUNT_RESUME_ERROR, pauseResumeErrorTracking);
    }
    toggleResumePlanModal(false);
  };

  const handleCancelPauseSubscription = async () => {
    const { isSuccess } = await pauseActions.cancelPause(activeSubscriptionId);

    if (isSuccess) {
      fetchSubscriptions();
      fetchSubscriptionStatus();
      fetchPayments(entitlementId);
    }
  };

  const handleApplyPromoCode = async ({ promoCode }) => {
    let isPaymentTypeError = false;
    try {
      const { isPaymentMethodError, isSuccess, errorMessage } = await applyPromoCode(promoCode);
      isPaymentTypeError = isPaymentMethodError;
      if (!isSuccess) {
        throw new Error(errorMessage);
      }
      await fetchPayments(entitlementId);
      return true;
    } catch (error) {
      if (isPaymentTypeError) {
        toggleApplyCouponPaymentModal(true);
      }
      return onSubmitReactFormError(error.message);
    }
  };

  const handleUpdatePaymentApplyCoupon = async (code) => {
    try {
      const { isSuccess, errorMessage } = await updateCardPayment();
      if (!isSuccess) {
        throw new Error(errorMessage);
      }
      toggleApplyCouponPaymentModal(false);
      await fetchPaymentMethods();
      await handleApplyPromoCode({ promoCode: code });
      return true;
    } catch (error) {
      return onSubmitReactFormError(error.message);
    }
  };

  return (
    <CenterFrameLayout title="My Account" showLoader={isLoading}>
      {myAccountPageTopContentCard && !promoBanner && (
        <div className="myAccountPageTopSection">
          <BrazeContentCard zone={MY_ACCOUNT_PAGE_TOP} />
        </div>
      )}
      <div className="settingsSection">
        {isConfirmSuccess && <ConfirmationMsg text={confirmationMsg} />}
        <ManageProfile user={user} subTitle="Account" routes={routes} trackEditAccount={trackEditAccount} />
        {promoBanner && (
          <SectionComponent
            subTitle="Promotion"
            sectionRight={<PromoBannerContainer promoBanner={promoBanner} useDetailsBanner={false} />}
          />
        )}
        {myAccountTopBanner && !promoBanner && (
          <SectionComponent subTitle="Notification" sectionRight={<BrazeContentCard zone={MY_ACCOUNT_TOP} />} />
        )}
        {!promoBanner && <RedfastZone name="MyAccountTop" />}
        <Membership
          title="Subscription"
          user={user}
          subscription={subscription}
          nextBillInfo={nextBillInfo}
          routes={routes}
          trackEditAccount={trackEditAccount}
          onRestartPlan={handleRestartPlan}
          toggleResumePlanModal={toggleResumePlanModal}
          handleCancelPauseSubscription={handleCancelPauseSubscription}
          isPauseEnabled={isPauseEnabled}
          activePlanSimplifiedName={activePlanSimplifiedName}
          activePlan={activePlan}
          allPlans={allPlans}
          openPlanSelectModal={showPlanSelectModal}
          navigate={navigate}
          state={state}
          payments={payments}
        />
        <ManageBilling
          title="Billing"
          user={user}
          subscription={subscription}
          paymentMethods={paymentMethods}
          nextBillInfo={nextBillInfo}
          hasPayments={hasPayments}
          routes={routes}
          trackEditAccount={trackEditAccount}
          orders={orders}
        />
        {myAccount2ndBanner && !promoBanner && (
          <SectionComponent subTitle="Promotion" sectionRight={<BrazeContentCard zone={MY_ACCOUNT_2ND} />} />
        )}
        {!promoBanner && <RedfastZone name="MyAccount2nd" />}
        {showAccountPromoCode && !subscription.isNewUser && !isPendingCancel && !isIAPUser && (
          <ManagePromotion ref={promoWrapperRef} onSubmitPromoCode={handleApplyPromoCode} />
        )}
      </div>
      {/* for reinstating pendingCancel - not for restarting churned */}
      {showReinstateModal && (
        <RestartPlanModal
          isShowing={showReinstateModal}
          onClose={() => toggleReinstateModal(false)}
          planName={activePlanSimplifiedName}
        />
      )}
      {showRestartChurnedModal && (
        <RestartConfirmationModal
          onClose={() => toggleRestartChurnedModal(false)}
          onSuccessClick={() => setLocation(rootDomain)}
          paymentMethodRequired={paymentMethodRequired}
          onPaymentSubmit={handleRestartChurnedPlan}
          showSuccessModal={showRestartChurnedModalSuccess}
        />
      )}
      {showResumePlanModal && (
        <ResumePlanModal
          isShowing={showResumePlanModal}
          onClose={() => toggleResumePlanModal(false)}
          handleResumeSubscription={handleResumeSubscription}
        />
      )}
      {showApplyCouponPaymentModal && (
        <UpdatePaymentModal
          isShowing={showApplyCouponPaymentModal}
          onPaymentSubmit={() => handleUpdatePaymentApplyCoupon(couponCode)}
          onClose={() => toggleApplyCouponPaymentModal(false)}
        />
      )}
    </CenterFrameLayout>
  );
}
